import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import ColumnFields from './ColumnFields';
import { ColumnDataType, TableColumn, UpdateColumnPayload } from '../../types/batchTables';

interface EditColumnContentProps {
  column: TableColumn;
  onConfirm: (data: UpdateColumnPayload, queryChanged: boolean) => void;
  onCancel: () => void;
}

const EditColumnContent: React.FC<EditColumnContentProps> = ({ column, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(column.name);
  const [query, setQuery] = useState(column.query || '');
  const [dataType, setDataType] = useState<ColumnDataType>(column.data_type);

  const handleSubmit = useCallback(() => {
    if (!name.trim()) return;

    const payload: UpdateColumnPayload = {};
    let queryChanged = false;

    if (name.trim() !== column.name) {
      payload.name = name.trim();
    }
    if (query.trim() !== (column.query || '')) {
      payload.query = query.trim() || undefined;
      queryChanged = true;
    }
    if (dataType !== column.data_type) {
      payload.data_type = dataType;
      queryChanged = true; // data_type change should also trigger re-process
    }

    // Only call if something changed
    if (Object.keys(payload).length > 0) {
      onConfirm(payload, queryChanged);
    } else {
      onCancel();
    }
  }, [name, query, dataType, column, onConfirm, onCancel]);

  const hasChanges =
    name.trim() !== column.name ||
    query.trim() !== (column.query || '') ||
    dataType !== column.data_type;

  return (
    <div className="w-[480px]">
      <div className="px-6 py-5 space-y-4">
        <ColumnFields
          idPrefix="edit-column"
          name={name}
          onNameChange={setName}
          query={query}
          onQueryChange={setQuery}
          dataType={dataType}
          onDataTypeChange={setDataType}
          autoFocus
        />

        {/* Re-process hint */}
        {(query.trim() !== (column.query || '') || dataType !== column.data_type) && (
          <div className="rounded-md bg-info-50 dark:bg-info-900/10 border border-info-200 dark:border-info-800 p-3">
            <p className="text-xs text-info-700 dark:text-info-300">
              {t('The query or data type has changed. After saving, you will be asked whether to re-process this column across all documents.')}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="neutral" size="small" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={!name.trim() || !hasChanges}
        >
          {t('Save changes')}
        </Button>
      </div>
    </div>
  );
};

export default EditColumnContent;
